import React, { useState, useEffect } from 'react';
import { Card, CardOffer } from '../../types';
import PlayableCard from './PlayableCard';
import { useSoundEffects } from '../../context/SoundEffectsContext';
import './FullScreenCardOffer.css';

interface FullScreenCardOfferProps {
  cardOffer: CardOffer;
  onAcceptCards: (selectedCardNames: string[]) => void;
  onSkip: () => void;
  canSkip?: boolean;
  requireAll?: boolean;
}

const FullScreenCardOffer: React.FC<FullScreenCardOfferProps> = ({
  cardOffer,
  onAcceptCards,
  onSkip,
  canSkip = true,
  requireAll = false,
}) => {
  const [selectedCards, setSelectedCards] = useState<string[]>([]);
  const [isVisible, setIsVisible] = useState(false);
  const { playSound } = useSoundEffects();

  useEffect(() => {
    playSound('new_card_available');
    requestAnimationFrame(() => {
      setIsVisible(true);
    });
  }, [playSound]);

  const toggleCardSelection = (card: Card) => {
    playSound('card_flip');
    if (selectedCards.includes(card.name)) {
      setSelectedCards(selectedCards.filter(name => name !== card.name));
    } else {
      if (cardOffer.max_selectable !== null && selectedCards.length >= cardOffer.max_selectable) {
        return;
      }
      setSelectedCards([...selectedCards, card.name]);
    }
  };

  const handleAccept = () => {
    setIsVisible(false);
    setTimeout(() => onAcceptCards(selectedCards), 300);
  };

  const handleSkip = () => {
    setIsVisible(false);
    setTimeout(onSkip, 300);
  };

  const maxCards = cardOffer.max_selectable;
  const hasLimit = maxCards !== null;
  const mustPickAll = requireAll && maxCards !== null;
  const remaining = mustPickAll ? maxCards - selectedCards.length : 0;
  const acceptDisabled = mustPickAll ? remaining > 0 : selectedCards.length === 0;

  return (
    <div className={`full-screen-card-offer ${isVisible ? 'visible' : ''}`}>
      {/* Floating sparkle particles */}
      <div className="sparkle-container">
        <div className="sparkle" />
        <div className="sparkle" />
        <div className="sparkle" />
        <div className="sparkle" />
        <div className="sparkle" />
        <div className="sparkle" />
        <div className="sparkle" />
        <div className="sparkle" />
      </div>

      <div className="full-screen-card-offer-top-bar">
        <button
          className={`full-screen-card-offer-skip ${!canSkip ? 'disabled' : ''}`}
          onClick={handleSkip}
          disabled={!canSkip}
        >
          <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
            <polyline points="6,9 12,15 18,9" />
          </svg>
          <span>Skip</span>
        </button>
      </div>

      <div className="full-screen-card-offer-content">
        <div className="full-screen-card-offer-header">
          <h2>New Cards Available!</h2>
          <p>Tap to select cards for your deck</p>
          {hasLimit && (
            <div className="selection-limit-badge">
              <span className="selection-count">{selectedCards.length}</span>
              <span className="selection-separator">/</span>
              <span className="selection-max">{maxCards}</span>
              <span className="selection-label">selected</span>
            </div>
          )}
        </div>

        <div className="offered-cards-grid">
          {cardOffer.offered_cards.map((card, index) => (
            <div
              key={`${card.name}-${index}`}
              className={`offered-card-wrapper ${selectedCards.includes(card.name) ? 'selected' : ''}`}
              style={{ animationDelay: `${index * 0.1}s` }}
            >
              <PlayableCard
                card={card}
                onClick={toggleCardSelection}
                disabled={false}
              />
              {card.description && (
                <p className="offered-card-description">{card.description}</p>
              )}
            </div>
          ))}
        </div>
      </div>

      <div className="full-screen-card-offer-bottom-bar">
        <button
          className={`full-screen-card-offer-accept ${acceptDisabled ? 'disabled' : ''}`}
          onClick={handleAccept}
          disabled={acceptDisabled}
        >
          {mustPickAll && remaining > 0
            ? `Pick ${remaining} more`
            : selectedCards.length === 0
              ? 'Select a card'
              : selectedCards.length === 1
                ? `Accept ${selectedCards[0].charAt(0).toUpperCase() + selectedCards[0].slice(1)}`
                : `Accept ${selectedCards.length} Cards`}
        </button>
      </div>
    </div>
  );
};

export default FullScreenCardOffer;
